import { useCallback, useEffect, useReducer } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  extractErrorMessage,
  useGetAllowedDomains,
  useGetOrganizationInvitedUsers,
  useGetUser,
  useInviteUser,
} from '@sib/api';
import { useUserSetupStore } from '@sib/auth';
import { useTranslation } from '@sib/i18n';
import { createTagFieldValidationStringMessage } from '@sib/ui';
import { useExpiringState } from '@spendbrain/common/hooks';
import { Invitee } from '@spendbrain/types/user';
import { z } from 'zod';

const MESSAGE_TIMEOUT = 6000;
export const MESSAGE_VISIBILITY_TIMEOUT = MESSAGE_TIMEOUT - 1000;

export enum DialogView {
  Main = 'main',
  ConfirmRemove = 'confirmRemove',
  ConfirmResend = 'confirmResend',
}

export enum ShareDialogFormField {
  EmailAddresses = 'emailAddresses',
}

type InviteesAction =
  | { type: 'replace'; value: Invitee[] }
  | { type: 'add'; value: Invitee[] }
  | { type: 'remove'; value: Invitee };

const inviteesReducer = (
  previousInvitees: Invitee[],
  action: InviteesAction
): Invitee[] => {
  if (action.type === 'replace') return action.value;

  if (action.type === 'remove')
    return previousInvitees.filter(
      invitee => invitee.email !== action.value.email
    );

  const nonDuplicateInvitees = action.value.filter(
    invitee =>
      !previousInvitees.some(
        previousInvitee => previousInvitee.email === invitee.email
      )
  );

  return [...nonDuplicateInvitees, ...previousInvitees];
};

interface UseValidationSchemaProps {
  invitees: Invitee[];
}

export const useValidationSchema = ({ invitees }: UseValidationSchemaProps) => {
  const { t } = useTranslation();
  const organizationId = useUserSetupStore(state => state.organization.id);
  const { data: allowedDomainsData } = useGetAllowedDomains(organizationId);

  const allowedDomains = allowedDomainsData?.items ?? [];
  const alreadyIncludedEmails = invitees.map(invitee => invitee.email);

  const isEmailDomainAllowed = (email: string): boolean => {
    const emailDomain = email.split('@')[1]?.toLowerCase();

    return allowedDomains.some(domain => domain.toLowerCase() === emailDomain);
  };

  return z.object({
    [ShareDialogFormField.EmailAddresses]: z.array(
      z
        .string()
        .refine(
          email => {
            const isValidEmail = z.string().email().safeParse(email).success;

            return isValidEmail;
          },
          email => ({
            message: createTagFieldValidationStringMessage({
              message: t('components.shareDialog.validation.invalidEmail'),
              variant: 'error',
              tag: email,
            }),
          })
        )
        .refine(
          email => isEmailDomainAllowed(email),
          email => ({
            message: createTagFieldValidationStringMessage({
              message: t('components.shareDialog.validation.invalidDomain', {
                domains: allowedDomains.join(', '),
              }),
              variant: 'error',
              tag: email,
            }),
          })
        )
        .refine(
          email => !alreadyIncludedEmails.includes(email),
          email => ({
            message: createTagFieldValidationStringMessage({
              message: t('components.shareDialog.validation.alreadyInvited'),
              variant: 'warning',
              tag: email,
            }),
          })
        )
    ),
  });
};

export type ShareDialogFormValues = z.infer<
  ReturnType<typeof useValidationSchema>
>;

const defaultValues: ShareDialogFormValues = {
  [ShareDialogFormField.EmailAddresses]: [],
};

interface UseFormPropsProps {
  invitees: Invitee[];
}

export const useFormProps = ({ invitees }: UseFormPropsProps) => {
  const validationSchema = useValidationSchema({ invitees });

  return useForm<ShareDialogFormValues>({
    defaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(validationSchema),
  });
};

interface UseOnSubmitProps {
  onSuccess?: (invitees: Invitee[]) => void;
}

export const useOnSubmit = ({ onSuccess }: UseOnSubmitProps) => {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] =
    useExpiringState<string>(MESSAGE_TIMEOUT);
  const [successMessage, setSuccessMessage] =
    useExpiringState<string>(MESSAGE_TIMEOUT);

  const { mutateAsync: inviteUser, ...rest } = useInviteUser({
    onSuccess: ({ success: addedUsers, failed: usersFailedToAdd }) => {
      if (addedUsers.length > 0) {
        const failedEmails = usersFailedToAdd.join(', ');

        setSuccessMessage(
          t(
            usersFailedToAdd.length === 0
              ? 'components.shareDialog.shareAccess.successMessage.description'
              : 'components.shareDialog.shareAccess.partialSuccessMessage.description',
            {
              count: addedUsers.length,
              emails: failedEmails,
            }
          )
        );

        return onSuccess?.(addedUsers);
      }

      //TODO: when adjusting invitation, check this logic with error message
      const error = usersFailedToAdd[0];

      setErrorMessage(
        error ||
          t('components.shareDialog.shareAccess.errorMessage.description')
      );
    },
    onError: error => {
      setErrorMessage(
        extractErrorMessage(
          error,
          t('components.shareDialog.shareAccess.errorMessage.description')
        )
      );
    },
  });
  const organizationId = useUserSetupStore(state => state.organization.id);
  const onSubmit = useCallback(
    async (values: ShareDialogFormValues) => {
      try {
        await inviteUser({
          emails: values[ShareDialogFormField.EmailAddresses],
          organizationId,
        });
      } catch (e) {
        return e;
      }
    },
    [inviteUser, organizationId]
  );

  return { ...rest, errorMessage, successMessage, onSubmit };
};

export const useInviteesList = () => {
  const [invitees, dispatchInvitees] = useReducer(inviteesReducer, []);
  const organizationId = useUserSetupStore(state => state.organization.id);
  const { data: currentUser } = useGetUser();
  const { isPending, isFetching, data } =
    useGetOrganizationInvitedUsers(organizationId);

  const onInviteesAdd = (invitees: Invitee[]) =>
    dispatchInvitees({ type: 'add', value: invitees });

  const onInviteeRemove = (invitee: Invitee) =>
    dispatchInvitees({ type: 'remove', value: invitee });

  useEffect(() => {
    if (data && currentUser) {
      const filteredUsers = data.filter(user => user.oid !== currentUser.id);

      const mappedInvitees: Invitee[] = filteredUsers.map(user => ({
        email: user.email,
        name: user.name,
        oid: user.oid,
        organization: user.organizations?.[0]?.name,
        userType: user.userType,
        userLoginStatus: user.userLoginStatus,
      }));
      dispatchInvitees({ type: 'replace', value: mappedInvitees });
    }
  }, [data, currentUser]);

  return {
    invitees,
    onInviteeRemove,
    onInviteesAdd,
    isLoading: isPending || isFetching,
  };
};
